/**
 * tokenize — split queries and haystacks into normalized tokens.
 *
 * Rules:
 *  - Lowercase + Unicode-normalize (NFKD) + strip combining marks.
 *  - Split on whitespace and `.`/`-`/`_`/`/` boundaries (palette ids
 *    use dots, file paths use slashes, kebab-case uses hyphens).
 *  - camelCase is split on lower→upper transitions.
 *  - Empty tokens dropped.
 */

const STRIP_MARKS = /[̀-ͯ]/g;
const HARD_BOUNDARY = /[\s./_\-:,;|]+/;

export function normalize(s: string): string {
  return s.normalize("NFKD").replace(STRIP_MARKS, "").toLowerCase();
}

/** Indices of word-start characters within the (already-normalized) string. */
export function wordStarts(s: string): boolean[] {
  const flags = new Array<boolean>(s.length).fill(false);
  if (s.length === 0) return flags;
  flags[0] = true;
  for (let i = 1; i < s.length; i++) {
    const prev = s.charCodeAt(i - 1);
    const cur = s.charCodeAt(i);
    // hard boundary in the original string
    if (
      prev === 32 /* space */ ||
      prev === 46 /* . */ ||
      prev === 45 /* - */ ||
      prev === 95 /* _ */ ||
      prev === 47 /* / */ ||
      prev === 58 /* : */ ||
      prev === 44 /* , */ ||
      prev === 124 /* | */
    ) {
      flags[i] = true;
      continue;
    }
    // camelCase / digit boundary (operating on the un-normalized form would
    // be more accurate; in normalized form everything is lower so this only
    // catches alpha↔digit which is still useful)
    const prevIsDigit = prev >= 48 && prev <= 57;
    const curIsDigit = cur >= 48 && cur <= 57;
    if (prevIsDigit !== curIsDigit) flags[i] = true;
  }
  return flags;
}

export function tokenizeQuery(q: string): string[] {
  if (!q) return [];
  return normalize(q)
    .split(HARD_BOUNDARY)
    .filter((t) => t.length > 0);
}
